import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ClipboardList, HeartHandshake, Users, ArrowRight, AlertTriangle,
  CheckSquare, CalendarClock, Play, Loader2, ListChecks, CheckCircle2, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const FUNDING_STYLE = {
  billable: "bg-emerald-50 text-emerald-700",
  volunteer: "bg-purple-50 text-purple-700",
  admin: "bg-gray-100 text-gray-700",
  ndis: "bg-blue-50 text-blue-700",
};

function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 0) return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  if (diff < 7) return d.toLocaleDateString("en-AU", { weekday: "long" });
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

function buildSummary({ tasks, todos, shifts, overdueCount }) {
  const parts = [];
  if (todos.length) parts.push(`${todos.length} ${todos.length === 1 ? "to-do" : "to-dos"}`);
  if (tasks.length) parts.push(`${tasks.length} ${tasks.length === 1 ? "task" : "tasks"}${overdueCount ? ` (${overdueCount} overdue)` : ""}`);
  if (shifts.length) parts.push(`${shifts.length} upcoming ${shifts.length === 1 ? "shift" : "shifts"}`);
  if (parts.length === 0) return "You're all caught up. Nothing scheduled in the next 2 weeks.";
  if (parts.length === 1) return `You have ${parts[0]}.`;
  return `You have ${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}.`;
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockInBusy, setClockInBusy] = useState(null);
  const [todoBusy, setTodoBusy] = useState(null);
  const [quickTodo, setQuickTodo] = useState("");
  const [quickTodoBusy, setQuickTodoBusy] = useState(false);
  const isAdminStaff = user?.role === "admin" || user?.role === "staff";
  const isAdmin = user?.role === "admin";
  const firstName = (user?.name || "").split(" ")[0];

  const load = () => {
    setLoading(true);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const end = new Date(today.getTime() + 14 * 86400000);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    Promise.all([
      isAdminStaff ? api.get("/dashboard/stats").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      api.get("/tasks", { params: { mine: true, status_filter: "open", from_date: fmt(today), to_date: fmt(end) } }).catch(() => ({ data: [] })),
      api.get("/shifts/scheduled", { params: { from_date: fmt(today), to_date: fmt(end), user_id: user?.id } }).catch(() => ({ data: [] })),
      api.get("/todos").catch(() => ({ data: [] })),
    ]).then(([s, t, sc, td]) => {
      setStats(s.data);
      setTasks((t.data || []).slice(0, 6));
      setShifts((sc.data || []).filter((x) => x.status === "scheduled" && x.user_id === user?.id).slice(0, 5));
      setTodos((td.data || []).filter((x) => x.user_id === user?.id && !x.completed).slice(0, 6));
      setLoading(false);
    });
  };
  useEffect(() => { if (user) load(); }, [user?.id]);

  const canClock = (s) => {
    const now = Date.now();
    const st = new Date(s.scheduled_start).getTime();
    const en = new Date(s.scheduled_end).getTime();
    return s.status === "scheduled" && now >= st - 15 * 60 * 1000 && now <= en;
  };
  const clockIn = async (s) => {
    setClockInBusy(s.id);
    try {
      await api.post("/me/shifts/clock-in", { shift_id: s.id });
      toast.success("Clocked in");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setClockInBusy(null); }
  };

  const toggleTodo = async (t) => {
    setTodoBusy(t.id);
    try {
      await api.patch(`/todos/${t.id}`, { completed: !t.completed });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTodoBusy(null); }
  };

  const addQuickTodo = async (e) => {
    e?.preventDefault();
    if (!quickTodo.trim()) return;
    setQuickTodoBusy(true);
    try {
      await api.post("/todos", { title: quickTodo.trim(), priority: "medium" });
      setQuickTodo("");
      load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setQuickTodoBusy(false); }
  };

  const overdueCount = useMemo(() => tasks.filter((t) => {
    const d = new Date(t.activity_date || t.due_date || "");
    d.setHours(0, 0, 0, 0);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return d < today;
  }).length, [tasks]);

  return (
    <div className="fade-in" data-testid="dashboard-home">
      <div className="mb-6">
        <div className="eyebrow text-njas-sage-hover text-[10px]">Your dashboard</div>
        <h1 className="font-serif text-3xl text-njas-navy">Hi {firstName || "there"}.</h1>
        <p className="text-sm text-njas-muted mt-1">
          {loading ? "Loading…" : buildSummary({ tasks, todos, shifts, overdueCount })}
        </p>
      </div>

      {/* Three-column personal panels: To-dos · Tasks · Shifts */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* To-do list */}
        <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid="home-my-todos">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-njas-sage-hover" />
              <div className="font-serif text-lg text-njas-navy">My to-do list</div>
            </div>
            <Link to="/dashboard/todos" className="text-xs text-njas-navy underline hover:text-njas-navy-hover">Open →</Link>
          </div>

          <form onSubmit={addQuickTodo} className="flex items-center gap-2 mb-3">
            <input
              value={quickTodo}
              onChange={(e) => setQuickTodo(e.target.value)}
              placeholder="Add a to-do…"
              className="flex-1 rounded-lg border border-[var(--njas-border)] bg-white px-3 py-1.5 text-sm placeholder:text-njas-muted focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage"
              data-testid="home-todo-input"
            />
            <button
              type="submit"
              disabled={quickTodoBusy || !quickTodo.trim()}
              data-testid="home-todo-add"
              className="inline-flex items-center gap-1 rounded-full bg-njas-navy text-white text-xs px-3 py-1.5 hover:bg-njas-navy-hover disabled:opacity-50"
            >
              {quickTodoBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            </button>
          </form>

          {loading && <div className="text-sm text-njas-muted">Loading…</div>}
          {!loading && todos.length === 0 && (
            <div className="text-sm text-njas-muted italic py-4 text-center">
              Nothing on your list. Add one above.
            </div>
          )}
          {!loading && todos.length > 0 && (
            <div className="space-y-1.5">
              {todos.map((t) => (
                <div key={t.id} data-testid={`home-todo-${t.id}`} className="flex items-start gap-2 group">
                  <button
                    onClick={() => toggleTodo(t)}
                    disabled={todoBusy === t.id}
                    aria-label="Mark complete"
                    data-testid={`home-todo-check-${t.id}`}
                    className="mt-0.5 w-4 h-4 rounded border-2 border-njas-muted/40 hover:border-njas-sage flex items-center justify-center shrink-0"
                  >
                    {todoBusy === t.id && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                  </button>
                  <Link to="/dashboard/todos" className="flex-1 min-w-0">
                    <div className="text-sm text-njas-navy truncate hover:text-njas-navy-hover">{t.title}</div>
                    {(t.due_date || t.priority === "high") && (
                      <div className="text-xs text-njas-muted flex items-center gap-1.5">
                        {t.priority === "high" && <span className="text-red-600">High</span>}
                        {t.due_date && <span>{fmtDay(t.due_date)}</span>}
                      </div>
                    )}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Tasks */}
        <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid="home-my-tasks">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-njas-sage-hover" />
              <div className="font-serif text-lg text-njas-navy">Client tasks</div>
            </div>
            <Link to="/dashboard/tasks" className="text-xs text-njas-navy underline hover:text-njas-navy-hover">Open →</Link>
          </div>
          {loading && <div className="text-sm text-njas-muted">Loading…</div>}
          {!loading && tasks.length === 0 && (
            <div className="text-sm text-njas-muted italic py-4 text-center">
              No open tasks in the next 2 weeks. Nice work.
            </div>
          )}
          {!loading && tasks.length > 0 && (
            <div className="space-y-2">
              {tasks.map((t) => {
                const overdue = new Date(t.activity_date || t.due_date) < new Date(new Date().setHours(0, 0, 0, 0));
                return (
                  <Link
                    key={t.id}
                    to={t.client_id ? `/dashboard/clients/${t.client_id}` : "/dashboard/tasks"}
                    data-testid={`home-task-${t.id}`}
                    className="flex items-start gap-3 rounded-lg p-2 hover:bg-njas-bg-alt/70 transition-colors border border-transparent hover:border-[var(--njas-border)]"
                  >
                    <div className={`w-1.5 self-stretch rounded-full ${overdue ? "bg-red-400" : "bg-njas-sage"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-njas-navy truncate">{t.title}</div>
                      <div className="text-xs text-njas-muted flex items-center gap-2 flex-wrap">
                        <span className={overdue ? "text-red-600 font-medium" : ""}>{fmtDay(t.activity_date || t.due_date)}</span>
                        {t.client_name && <><span>·</span><span className="truncate">{t.client_name}</span></>}
                        {t.funding_type && <span className={`px-1.5 py-0.5 rounded text-[10px] ${FUNDING_STYLE[t.funding_type] || "bg-gray-100 text-gray-700"}`}>{t.funding_type}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* My Shifts */}
        <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid="home-my-shifts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-njas-sage-hover" />
              <div className="font-serif text-lg text-njas-navy">Upcoming shifts</div>
            </div>
            <Link to="/dashboard/roster" className="text-xs text-njas-navy underline hover:text-njas-navy-hover">Open →</Link>
          </div>
          {loading && <div className="text-sm text-njas-muted">Loading…</div>}
          {!loading && shifts.length === 0 && (
            <div className="text-sm text-njas-muted italic py-4 text-center">
              {isAdmin ? "No rostered shifts. Head to the Roster to schedule."
              : "No shifts rostered yet."}
            </div>
          )}
          {!loading && shifts.length > 0 && (
            <div className="space-y-2">
              {shifts.map((s) => {
                const can = canClock(s);
                const start = new Date(s.scheduled_start);
                return (
                  <div key={s.id} data-testid={`home-shift-${s.id}`} className="flex items-center gap-2 rounded-lg border border-[var(--njas-border)] bg-njas-bg-alt/40 p-2">
                    <div className="w-12 text-center shrink-0">
                      <div className={`text-[10px] uppercase tracking-wider ${fmtDay(s.scheduled_start) === "Today" ? "text-njas-sage-hover font-semibold" : "text-njas-muted"}`}>{fmtDay(s.scheduled_start)}</div>
                      <div className="text-xs font-medium text-njas-navy">
                        {start.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-njas-navy truncate">{s.client_name || "General"}</div>
                    </div>
                    <button
                      onClick={() => clockIn(s)}
                      disabled={!can || clockInBusy === s.id}
                      data-testid={`home-clock-in-${s.id}`}
                      title={!can ? "Available 15 min before start" : "Clock in"}
                      className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2.5 py-1 ${
                        can ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      } disabled:opacity-60`}
                    >
                      {clockInBusy === s.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Play className="w-2.5 h-2.5" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Admin/Staff-only referral stats */}
      {isAdminStaff && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="New Referrals" value={stats?.referrals?.new} icon={ClipboardList} tint="bg-blue-50 text-blue-700" testid="stat-new" />
            <StatCard label="In Review" value={stats?.referrals?.in_review} icon={ClipboardList} tint="bg-yellow-50 text-yellow-700" testid="stat-review" />
            <StatCard label="Accepted" value={stats?.referrals?.accepted} icon={ClipboardList} tint="bg-emerald-50 text-emerald-700" testid="stat-accepted" />
            <StatCard label="Closed" value={stats?.referrals?.closed} icon={ClipboardList} tint="bg-gray-100 text-gray-700" testid="stat-closed" />
          </div>

          {!!stats?.referrals?.crisis && (
            <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-5 flex items-center gap-4" data-testid="crisis-banner">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div className="flex-1">
                <div className="font-medium text-red-800">{stats.referrals.crisis} referral(s) marked as CRISIS</div>
                <div className="text-sm text-red-700">Prioritise review immediately.</div>
              </div>
              <Link to="/dashboard/referrals" className="text-sm font-medium text-red-700 hover:underline">Review →</Link>
            </div>
          )}

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <LinkCard to="/dashboard/referrals" icon={ClipboardList} label="Referrals" count={stats?.referrals?.total} />
            <LinkCard to="/dashboard/volunteers" icon={HeartHandshake} label="Volunteers" count={stats?.volunteers} />
            <LinkCard to="/dashboard/members" icon={Users} label="Members" count={stats?.members} />
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="rounded-xl bg-white border border-[var(--njas-border)] p-6" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-4 text-3xl font-serif text-njas-navy">{value ?? "—"}</div>
      <div className="text-xs text-njas-muted mt-1">{label}</div>
    </div>
  );
}

function LinkCard({ to, icon: Icon, label, count }) {
  return (
    <Link to={to} className="group rounded-xl bg-white border border-[var(--njas-border)] p-6 hover:border-njas-sage transition-colors">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-njas-muted text-xs uppercase tracking-wider">{label}</div>
          <div className="mt-2 font-serif text-3xl text-njas-navy">{count ?? "—"}</div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-njas-sage-tint text-njas-sage-hover flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-6 text-sm text-njas-navy font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
        Open <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}
